/**
 * tripFormat.ts — how the planner writes drive times, distances and clock
 * times. One format everywhere (summary line, day headers, travel strips) so
 * the same drive never reads two ways.
 *
 *   formatDuration(220)  → "3 h 40 m"     formatDuration(45) → "45 min"
 *   formatKm(318.4)      → "318 km"       formatKm(1030)     → "1,030 km"
 *   formatClock(760)     → "12:40 pm"
 */

export function formatDuration(minutes?: number | null): string | null {
  if (typeof minutes !== 'number' || !Number.isFinite(minutes) || minutes < 0) return null;
  const m = Math.round(minutes);
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  const rest = m % 60;
  return rest ? `${h} h ${rest} m` : `${h} h`;
}

export function formatKm(km?: number | null): string | null {
  if (typeof km !== 'number' || !Number.isFinite(km) || km < 0) return null;
  if (km < 10) return `${km.toFixed(1)} km`;
  return `${Math.round(km).toLocaleString('en-AU')} km`;
}

/** Minutes after midnight → "9:00 am". Past midnight wraps and says so. */
export function formatClock(minutesAfterMidnight?: number | null): string | null {
  if (typeof minutesAfterMidnight !== 'number' || !Number.isFinite(minutesAfterMidnight)) return null;
  const total = Math.round(minutesAfterMidnight);
  const dayRoll = Math.floor(total / 1440);
  const inDay = ((total % 1440) + 1440) % 1440;
  const h24 = Math.floor(inDay / 60);
  const mm = String(inDay % 60).padStart(2, '0');
  const suffix = h24 < 12 ? 'am' : 'pm';
  const h12 = h24 % 12 === 0 ? 12 : h24 % 12;
  return `${h12}:${mm} ${suffix}${dayRoll > 0 ? ' next day' : ''}`;
}

/** "Sat 18 Oct" for a YYYY-MM-DD string (local noon, so no timezone slip). */
export function formatShortDate(iso?: string | null): string | null {
  if (!iso || !/^\d{4}-\d{2}-\d{2}$/.test(iso)) return null;
  const d = new Date(`${iso}T12:00:00`);
  if (Number.isNaN(d.getTime())) return null;
  const weekday = d.toLocaleDateString('en-AU', { weekday: 'short' });
  const day = d.getDate();
  const month = d.toLocaleDateString('en-AU', { month: 'short' });
  return `${weekday} ${day} ${month}`;
}

/**
 * A place name short enough for a chip: "Lakes Entrance" → "Lakes Ent.",
 * "Port Macquarie" → "Port Mac.", "Eden" → "Eden". Drops anything after a
 * comma ("Eden, NSW").
 */
export function shortPlaceName(name?: string | null, max = 11): string {
  const bare = (name || '').split(',')[0].replace(/\s+/g, ' ').trim();
  if (bare.length <= max) return bare;
  const words = bare.split(' ');
  if (words.length >= 2) {
    const abbr = `${words[0]} ${words[1].slice(0, 3)}.`;
    if (abbr.length <= max + 1) return abbr;
  }
  return `${bare.slice(0, max - 1).trimEnd()}.`;
}

/** "Sat 17" for a YYYY-MM-DD (the chip's short date). */
export function formatChipDate(iso?: string | null): string | null {
  const full = formatShortDate(iso);
  return full ? full.split(' ').slice(0, 2).join(' ') : null;
}

/** "3 h 40 m · 318 km" — whichever halves exist. */
export function formatDriveFigures(minutes?: number | null, km?: number | null): string | null {
  const parts = [formatDuration(minutes), formatKm(km)].filter(Boolean);
  return parts.length ? parts.join(' · ') : null;
}
